import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Brand } from './brand';
import { alert, AlertType, Input } from '@quyva/shared-ui';
import { PoweredByInfo } from './powereByComponent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRightToBracket,
    faEye,
    faEyeSlash,
    faUserPlus,
    faUserLock,
} from '@fortawesome/free-solid-svg-icons';
import googleLogo from '../images/google2.png';
import { useAuth } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { getUrl } from './pdfDisplay/utils';
import {
    Features,
    OtherFeatures,
    getEmailPattern,
    isEmailValid,
    isFeatureEnabled,
} from '@quyva/common';
import { useFeatureFlags } from '../context';
import { useGlobalCredits } from '../context/GlobalStateContext.js';
import useDeviceDetect from './useDeviceDetect';
import { MicrosoftSignIn } from './auth/microsoftSignin.jsx';

const Login = () => {
    const { featureFlagsData } = useFeatureFlags();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [emailValid, setEmailValid] = useState(true);
    const [passwordValid, setPasswordValid] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const path = location.pathname;
    const { login } = useAuth();
    const [inviteToken, setInviteToken] = useState(null);
    const [invitedOrgId, setInvitedOrgId] = useState(null);
    const [invitedAccessRights, setInvitedAccessRights] = useState(null);
    const { fetchUserPreferences } = useGlobalCredits();
    const { isMobile } = useDeviceDetect();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const orgId = params.get('org');
        const access_rights = params.get('access_rights');

        if (token && orgId) {
            setInviteToken(token);
            setInvitedOrgId(orgId);
            setInvitedAccessRights(access_rights);
        }
    }, [location.search]);

    /**
     * Handle authentication success
     */
    const handleSuccess = (authResult) => {
        login(authResult);
    };

    /**
     * Handle authentication error
     */
    const handleError = (error) => {
        console.error('❌ Authentication error:', error);

        alert(
            AlertType.ERROR,
            error.message || 'Authentication failed. Please try again.'
        );
    };

    const googleLogin = useGoogleLogin({
        onSuccess: (credentialResponse) => {
            async function sendData() {
                // console.log(credentialResponse);

                try {
                    const dynamicUrl = getUrl(
                        `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/google_login`
                    );

                    const response = await fetch(dynamicUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(credentialResponse),
                        credentials: 'include',
                    });

                    if (response.ok) {
                        const userData = await response.json();
                        // console.log('Login successful', userData);
                        login(userData);

                        // Redirect mobile users to chatapp
                        if (isMobile) {
                            navigate('/chatapp/repos');
                        }
                    } else {
                        const errorData = await response.json();
                        // console.error('Login failed', errorData);
                        alert(
                            AlertType.ERROR,
                            `Login failed: ${errorData.message}`
                        );
                    }
                } catch (error) {
                    console.log(`Google login error:\n`, error);
                }
            }
            sendData();
        },
        onError: () => {
            alert(AlertType.ERROR, 'Login through google Failed');
        },
        flow: 'auth-code',
    });

    const Finalize = async (e) => {
        e.preventDefault();
        try {
            if (!email && !password) {
                setEmailValid(false);
                setPasswordValid(false);
            }
            if (!email) {
                alert(AlertType.ERROR, 'Email address field is empty!');
                setEmailValid(false);
                return;
            } else {
                setEmailValid(true);
            }
            if (isEmailValid(email)) {
                setEmailValid(true);
            } else {
                alert(AlertType.ERROR, 'Please enter a valid email address!');
                setEmailValid(false);
                return;
            }

            if (!password) {
                alert(AlertType.ERROR, 'Password field is empty!');
                setPasswordValid(false);
                return;
            } else {
                setPasswordValid(true);
            }

            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/new_login`
            );

            const requestBody = {
                email,
                password,
                inviteToken,
                invitedOrgId,
                invitedAccessRights,
            };

            const response = await fetch(dynamicUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
                credentials: 'include',
            });

            if (response.ok) {
                const userData = await response.json();
                // console.log('Login successful', userData);

                // Force password change for unsigned users created by super admin
                // Do NOT call login() — user is not authenticated until password is changed
                if (userData.forceChangeToken) {
                    navigate(`/forgot-password/reset?token=${userData.forceChangeToken}`);
                    return;
                }

                login(userData);

                // Immediately refresh preferences so navbar gets updated
                await fetchUserPreferences();

                // Redirect mobile users to chatapp
                if (isMobile) {
                    navigate('/chatapp/repos');
                } else {
                    // Desktop users go to regular chats
                    // navigate('/chats');
                }
            } else {
                const errorData = await response.json();
                // console.error('Login failed', errorData);
                alert(AlertType.ERROR, `Login failed: ${errorData.error}`);
            }
        } catch (error) {
            // console.error('Error during login', error);
            alert(
                AlertType.ERROR,
                'An error occurred during login. Please try again.'
            );
        }
    };

    return (
        <div className='flex items-center justify-center min-h-screen bg-gradient-primary'>
            <div className='w-full max-w-[500px] flex-col border bg-backgrounds-white p-6 shadow-lg rounded-xl'>
                <div
                    className='mb-8 w-full flex justify-center items-center'
                    onClick={() => {
                        if (path !== '/login') {
                            navigate('/login');
                        }
                    }}
                >
                    <div className='w-fit h-fit'>
                        {isFeatureEnabled(
                            [Features.OTHERS, OtherFeatures.LOGO],
                            featureFlagsData
                        ) && <Brand iconClassName={'w-20 h-20 mr-3'} />}
                    </div>
                </div>
                <div className='flex flex-col text-sm w-full'>
                    <form onSubmit={Finalize}>
                        <div className='w-full mb-5'>
                            <Input
                                className={`rounded-xl border p-3 w-full hover:outline-none focus:outline-backgrounds-primary300 hover:border-backgrounds-primary300 shadow-sm ${
                                    !emailValid && 'border-red-500'
                                }`}
                                type='email'
                                placeholder='Email id'
                                pattern={getEmailPattern().source}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div className='relative w-full mb-5'>
                            <Input
                                className={`rounded-xl border p-3 w-full hover:outline-none focus:outline-backgrounds-primary300 hover:border-backgrounds-primary300 shadow-sm ${
                                    !passwordValid && 'border-red-500'
                                }`}
                                type={showPassword ? 'text' : 'password'}
                                placeholder='Password'
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            {password && (
                                <FontAwesomeIcon
                                    icon={showPassword ? faEyeSlash : faEye}
                                    className='absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer font-semibold text-texts-secondary300'
                                    onClick={() =>
                                        setShowPassword(!showPassword)
                                    }
                                />
                            )}
                        </div>
                        <button
                            className='flex w-full font-semibold items-center justify-center bg-buttons-primary border border-1 shadow-inner hover:bg-gradient-buttons hover:border-buttons-tertiary hover:text-texts-primary p-3 mt-2 rounded-lg'
                            type='submit'
                            // onClick={Finalize}
                        >
                            <FontAwesomeIcon
                                icon={faRightToBracket}
                                className='mr-1.5 drop-shadow-sm text-texts-primary'
                            />
                            <span className='drop-shadow-sm text-texts-primary'>
                                Login
                            </span>
                        </button>
                    </form>
                    <div className='mt-3'>
                        <button
                            className='w-full rounded-lg border px-3 py-2.5 mt-1  flex justify-center items-center text-texts-secondary200 shadow-inner hover:bg-backgrounds-primary100'
                            onClick={() => googleLogin()}
                        >
                            <span>
                                <img
                                    className='max-w-3 max-h-3 mr-2'
                                    src={googleLogo}
                                    alt='camera'
                                    draggable='false'
                                />
                            </span>
                            Sign in with Google
                        </button>
                    </div>
                    <div className='mt-3'>
                        {/* Microsoft Sign-In Component */}
                        <MicrosoftSignIn
                            onSuccess={handleSuccess}
                            onError={handleError}
                            currency='INR' // Optional, defaults to INR
                        />
                    </div>
                </div>

                <div className='mt-4 p-1 flex justify-between text-sm font-semibold text-texts-tertiary'>
                    <Link to='/forgot-password'>
                        <span className='hover:text-texts-tertiary400'>
                            <FontAwesomeIcon
                                icon={faUserLock}
                                className='mr-1.5'
                            />
                            Forgot password?
                        </span>
                    </Link>
                    <Link to='/signup'>
                        <span className='hover:text-texts-tertiary400'>
                            <FontAwesomeIcon
                                icon={faUserPlus}
                                className='mr-1.5'
                            />
                            Sign up
                        </span>
                    </Link>
                </div>

                <div className='w-full flex justify-center items-center mt-8'>
                    <PoweredByInfo />
                </div>
            </div>
        </div>
    );
};

export default Login;
